import type { FC, ReactNode, RefObject } from 'react'
import type { Hill } from '@/lib/contours'
import type { Hole } from '@/types'

import { cn } from '@/lib/utils'
import { seededRandom } from '@/lib/contours'
import { ContourPaths } from '@/components/generals/contour-field'
import { clubhousePosition, holes } from '@/constants/course'

export const MAP_WIDTH = 1000
export const MAP_HEIGHT = 640

const SEA = 'M0 0H86C70 70 104 132 84 204C66 272 56 330 80 402C100 468 70 540 80 640H0Z'
const COASTLINE = 'M86 0C70 70 104 132 84 204C66 272 56 330 80 402C100 468 70 540 80 640'
const SURF = [
    'M72 0C56 70 90 132 70 204C52 272 42 330 66 402C86 468 56 540 66 640',
    'M56 0C40 70 74 132 54 204C36 272 26 330 50 402C70 468 40 540 50 640'
]

const DUNES: [number, number][] = [
    [104, 42], [114, 96], [104, 152], [98, 238], [102, 300],
    [94, 360], [106, 424], [102, 492], [96, 562], [104, 612]
]

const NORTH_LAKE = 'M338 140C340 124 366 118 388 124C408 130 414 146 402 156C388 166 352 164 342 156C336 151 337 146 338 140Z'
const RESERVOIR = 'M712 446C716 424 740 410 766 410C794 410 814 424 820 444C826 464 814 482 794 486C782 488 780 474 772 468C762 462 750 472 746 482C738 496 718 490 712 476C708 466 710 456 712 446Z'
const QUARRY = 'M438 188L452 180L470 186L474 204L458 212L442 206Z'

const MAP_HILLS: Hill[] = [
    { x: 470, y: 210, radius: 120, rings: 8, seed: 1.7, stretch: 1.5 },
    { x: 780, y: 170, radius: 110, rings: 7, seed: 3.3 },
    { x: 300, y: 380, radius: 90, rings: 6, seed: 2.2 },
    { x: 640, y: 470, radius: 100, rings: 7, seed: 4.6 },
    { x: 170, y: 120, radius: 80, rings: 5, seed: 0.9 },
    { x: 900, y: 520, radius: 90, rings: 6, seed: 5.8 }
]

const TREE_CLUSTERS = [
    { x: 250, y: 488, radius: 13, count: 9 },
    { x: 272, y: 552, radius: 13, count: 9 },
    { x: 176, y: 350, radius: 20, count: 14 },
    { x: 236, y: 58, radius: 34, count: 24 },
    { x: 150, y: 190, radius: 30, count: 18 },
    { x: 912, y: 110, radius: 40, count: 26 },
    { x: 940, y: 250, radius: 34, count: 20 },
    { x: 548, y: 300, radius: 26, count: 14 },
    { x: 430, y: 405, radius: 20, count: 11 },
    { x: 700, y: 590, radius: 26, count: 13 }
]

const round = (value: number): number => Math.round(value * 10) / 10

/** Tree canopies scattered deterministically, so server and browser agree. */
const TREES = TREE_CLUSTERS.flatMap((cluster, clusterIndex) => {
    const random = seededRandom(clusterIndex * 131 + 17)
    const trees: { x: number, y: number, r: number }[] = []
    let guard = 0

    while (trees.length < cluster.count && guard < 400) {
        guard += 1
        const dx = (random() * 2 - 1) * cluster.radius
        const dy = (random() * 2 - 1) * cluster.radius
        if (dx * dx + dy * dy > cluster.radius * cluster.radius) continue

        trees.push({
            x: round(cluster.x + dx),
            y: round(cluster.y + dy),
            r: round(2.6 + random() * 2.8)
        })
    }

    return trees
})

export const holePath = (hole: Hole): string => {
    return `M${hole.tee[0]} ${hole.tee[1]}Q${hole.control[0]} ${hole.control[1]} ${hole.green[0]} ${hole.green[1]}`
}

/** Hole number badges sit just behind each tee, pointing away from the hole. */
const LABELS = holes.map((hole) => {
    const dx = hole.tee[0] - hole.control[0]
    const dy = hole.tee[1] - hole.control[1]
    const length = Math.sqrt(dx * dx + dy * dy) || 1

    return {
        x: round(hole.tee[0] + (dx / length) * 15),
        y: round(hole.tee[1] + (dy / length) * 15)
    }
})

interface CourseMapProps {
    activeIndex: number
    svgRef: RefObject<SVGSVGElement | null>
    routeRefs: RefObject<(SVGPathElement | null)[]>
    ballRef: RefObject<SVGGElement | null>
    className?: string
}

export const CourseMap: FC<CourseMapProps> = ({
    activeIndex,
    svgRef,
    routeRefs,
    ballRef,
    className
}): ReactNode => {
    const activeHole = holes[activeIndex]

    return (
        <svg
            ref={svgRef}
            viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
            preserveAspectRatio='xMidYMid meet'
            role='img'
            aria-labelledby='course-map-title'
            className={cn('select-none', className)}
        >
            <title id='course-map-title'>
                Routing map of the eighteen-hole championship course, from the coast to the clubhouse
            </title>

            <defs>
                <pattern id='map-water' width='7' height='7' patternUnits='userSpaceOnUse'>
                    <path d='M0 3.5h7' stroke='#aab4ab' strokeWidth='0.6' />
                </pattern>

                <pattern id='map-hatch' width='5' height='5' patternUnits='userSpaceOnUse' patternTransform='rotate(45)'>
                    <path d='M0 0v5' stroke='#978e7e' strokeWidth='0.9' />
                </pattern>
            </defs>

            <rect width={MAP_WIDTH} height={MAP_HEIGHT} fill='#f5f2eb' />

            <g className='text-stone'>
                <ContourPaths hills={MAP_HILLS} strokeWidth={0.8} />
            </g>

            <path d={SEA} fill='#d9ddd4' />
            <path d={SEA} fill='url(#map-water)' />
            {SURF.map((d) => (
                <path key={d} d={d} fill='none' stroke='#eef0ea' strokeWidth='1' strokeDasharray='10 14' />
            ))}
            <path d={COASTLINE} fill='none' stroke='#e2d5b8' strokeWidth='7' strokeLinecap='round' />

            <g fill='none' stroke='#b9ae98' strokeWidth='1' strokeLinecap='round'>
                {DUNES.map(([x, y]) => (
                    <path key={`${x}-${y}`} d={`M${x} ${y}q10 -7 20 0M${x + 4} ${y + 7}q7 -5 14 0`} />
                ))}
            </g>

            <path d={NORTH_LAKE} fill='#d4dbd2' stroke='#aab4ab' strokeWidth='1' />
            <path d={RESERVOIR} fill='#d4dbd2' stroke='#aab4ab' strokeWidth='1' />
            <path d={QUARRY} fill='url(#map-hatch)' stroke='#978e7e' strokeWidth='1' />

            <g fill='#1e3a2c'>
                {TREES.map((tree, index) => (
                    <circle key={index} cx={tree.x} cy={tree.y} r={tree.r} fillOpacity='0.17' />
                ))}
                <circle cx='856' cy='214' r='6.5' fillOpacity='0.4' />
            </g>

            <g fill='none' strokeLinecap='round'>
                {holes.map((hole, index) => (
                    <path
                        key={hole.number}
                        d={holePath(hole)}
                        stroke={index === activeIndex ? '#aeb892' : '#cdd2ba'}
                        strokeWidth='17'
                        className='transition-[stroke] duration-500'
                    />
                ))}
            </g>

            <g>
                {holes.map((hole, index) => (
                    <g key={hole.number}>
                        <rect
                            x={hole.tee[0] - 3.5}
                            y={hole.tee[1] - 3.5}
                            width='7'
                            height='7'
                            fill='#f5f2eb'
                            stroke='#1e3a2c'
                            strokeWidth='1'
                        />
                        <circle
                            cx={hole.green[0]}
                            cy={hole.green[1]}
                            r='9'
                            fill={index === activeIndex ? '#9fae82' : '#b8c29e'}
                            stroke='#1e3a2c'
                            strokeWidth='0.8'
                        />
                    </g>
                ))}
            </g>

            <g fill='none' strokeLinecap='round'>
                {holes.map((hole, index) => (
                    <path
                        key={hole.number}
                        ref={(element) => {
                            if (routeRefs.current) routeRefs.current[index] = element
                        }}
                        d={holePath(hole)}
                        stroke={index === activeIndex ? '#a5683f' : '#1e3a2c'}
                        strokeOpacity={index === activeIndex ? 1 : 0.7}
                        strokeWidth={index === activeIndex ? 2.4 : 1.5}
                    />
                ))}
            </g>

            <g>
                {holes.map((hole, index) => (
                    <g key={hole.number} opacity={index === activeIndex ? 1 : 0.55}>
                        <path
                            d={`M${hole.green[0]} ${hole.green[1]}v-17`}
                            stroke='#1b1b19'
                            strokeWidth='1'
                        />
                        <path
                            d={`M${hole.green[0]} ${hole.green[1] - 17}l9 3l-9 3z`}
                            fill={index === activeIndex ? '#a5683f' : '#1b1b19'}
                        />
                    </g>
                ))}
            </g>

            <g className='font-mono'>
                {holes.map((hole, index) => (
                    <g key={hole.number}>
                        <circle
                            cx={LABELS[index].x}
                            cy={LABELS[index].y}
                            r='7.5'
                            fill={index === activeIndex ? '#a5683f' : '#1b1b19'}
                        />
                        <text
                            x={LABELS[index].x}
                            y={LABELS[index].y}
                            dy='2.9'
                            textAnchor='middle'
                            fontSize='8'
                            fill='#f2eee6'
                        >
                            {hole.number}
                        </text>
                    </g>
                ))}
            </g>

            <g transform={`translate(${clubhousePosition.x} ${clubhousePosition.y})`}>
                <rect x='-14' y='-6' width='28' height='12' fill='#1b1b19' rx='1.5' />
                <rect x='-10' y='-2.5' width='20' height='5' fill='#c9a26b' rx='1' />
                <text y='22' textAnchor='middle' fontSize='8' fill='#1b1b19' className='font-mono' letterSpacing='1.2'>
                    CLUBHOUSE
                </text>
            </g>

            <circle
                cx={activeHole.green[0]}
                cy={activeHole.green[1]}
                r='10'
                fill='none'
                stroke='#a5683f'
                strokeWidth='1.2'
                className='animate-pulse-ring [transform-box:fill-box] [transform-origin:center]'
            />

            <g ref={ballRef} transform={`translate(${holes[0].tee[0]} ${holes[0].tee[1]})`}>
                <circle r='11' fill='#ffffff' fillOpacity='0.35' />
                <circle r='4.6' fill='#ffffff' stroke='#1b1b19' strokeWidth='1.2' />
            </g>
        </svg>
    )
}
